const configured = import.meta.env.VITE_API_URL as string | undefined;
export const API_URL = configured ?? '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public payload?: unknown,
  ) {
    super(message);
  }
}

const inFlightGets = new Map<string, Promise<unknown>>();
const conditionalCache = new Map<string, { etag: string; payload: unknown }>();

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const requestKey = `${method}:${path}`;
  if (method === 'GET' && inFlightGets.has(requestKey))
    return inFlightGets.get(requestKey) as Promise<T>;
  const operation = apiRequest<T>(path, init, method, requestKey);
  if (method === 'GET') {
    inFlightGets.set(requestKey, operation);
    void operation.then(
      () => inFlightGets.delete(requestKey),
      () => inFlightGets.delete(requestKey),
    );
  }
  return operation;
}

async function apiRequest<T>(
  path: string,
  init: RequestInit,
  method: string,
  requestKey: string,
): Promise<T> {
  const request = async (csrf: string | null) => {
    const headers = new Headers(init.headers);
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    if (csrf) headers.set('X-CSRF-Token', csrf);
    const cached = conditionalCache.get(requestKey);
    if (method === 'GET' && cached?.etag) headers.set('If-None-Match', cached.etag);
    const response = await fetch(`${API_URL}${path}`, {
      ...init,
      credentials: 'include',
      headers,
    });
    const payload =
      response.status === 304 && cached
        ? cached.payload
        : ((await response.json().catch(() => ({}))) as any);
    return { response, payload };
  };

  let result = await request(sessionStorage.getItem('aot_csrf'));
  if (
    method !== 'GET' &&
    path !== '/v1/auth/csrf' &&
    result.response.status === 403 &&
    result.payload.error?.code === 'CSRF_REJECTED'
  ) {
    const refresh = await fetch(`${API_URL}/v1/auth/csrf`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const refreshPayload = (await refresh.json().catch(() => ({}))) as any;
    if (refresh.ok && typeof refreshPayload.csrfToken === 'string') {
      sessionStorage.setItem('aot_csrf', refreshPayload.csrfToken);
      result = await request(refreshPayload.csrfToken);
    }
  }

  const { response, payload } = result;
  if (!response.ok && response.status !== 304)
    throw new ApiError(response.status, payload.error?.message ?? 'Request failed', payload);
  const etag = response.headers.get('ETag');
  if (method === 'GET' && etag) conditionalCache.set(requestKey, { etag, payload });
  return payload as T;
}
